import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ScanLine, Plus, Trash2, Loader2, CheckCircle2, XCircle, Wifi, WifiOff, Wrench, Flame, BookmarkCheck, Globe,
} from "lucide-react";
import { API, useAuth, useSharedLiveStream, TickerCard, LiveIndicator, SkipReasonsWidget } from "@/App";
import { useI18n } from "@/i18n";
import FreshnessIndicator from "@/components/FreshnessIndicator";
import { SkeletonList } from "@/components/Skeleton";
import { WatchlistPicker, ManageWatchlistsModal, useActiveWatchlist } from "@/components/WatchlistPicker";
import { toast } from "sonner";

// Phase 6B.1 / Iter98 + Iter98.1 — Operator Dashboard Final Clean Pass.

// Iter113.1 — Outcome-pulse footer chip helpers.  Shows the
// operator the most recent Strike Decision they recorded so they
// can recover context in 30 seconds.  Pure read-only: fetches
// `/api/forge/journal/decisions?limit=1` on mount, no polling.
const STATUS_COLORS = {
  ACCEPT: "#00FF88",
  CAUTION: "#FFB800",
  CAUTION_DATA_STALE: "#FFB800",
  REJECT: "#FF5577",
  BLOCKED: "#FF5577",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const delta = Math.max(0, Date.now() - then);
  const m = Math.floor(delta / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Iter115.9 — Build-info footer chip.  Tiny operator-confidence
// signal: pulls `/api/health` once on mount and renders the live
// service version + Mongo status dot + human-readable uptime.
// Pure read-only, no polling.  When `/api/health` is unreachable
// renders a red "offline" dot so the operator instantly sees that
// the backend is down (vs. just a UI bug).
function formatUptime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) {
    const mr = m % 60;
    return mr ? `${h}h ${mr}m` : `${h}h`;
  }
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return hr ? `${d}d ${hr}h` : `${d}d`;
}

function BuildInfoChip({ health, error }) {
  // `health` is the raw /api/health body; `error` is true when the
  // fetch itself failed (network/5xx with no parseable body).
  const offline = !!error || !health;
  const degraded = !offline && health.status !== "ok";
  const dotColor = offline
    ? "#FF3366"
    : degraded
    ? "#FFB800"
    : "#00FF88";
  const label = offline
    ? "offline"
    : degraded
    ? "degraded"
    : "live";
  const version = health?.version || "—";
  const uptime = health?.uptime_seconds != null ? formatUptime(health.uptime_seconds) : null;
  return (
    <div
      data-testid="build-info-chip"
      className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] border border-[#26262B] px-2.5 py-1 rounded-sm select-none"
      title={offline
        ? "Backend unreachable — /api/health failed"
        : `service: ${health?.service || "—"} · status: ${health?.status} · db: ${health?.db ? "ok" : "down"}`}
    >
      <span
        data-testid="build-info-dot"
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: dotColor, boxShadow: `0 0 4px ${dotColor}80` }}
      />
      <span data-testid="build-info-label">{label}</span>
      <span className="text-[#3F3F47]">·</span>
      <span data-testid="build-info-version" className="text-[#E0E0E0]">v{version}</span>
      {uptime && (
        <>
          <span className="text-[#3F3F47]">·</span>
          <span data-testid="build-info-uptime" className="text-[#5F5F66] normal-case tracking-normal">up {uptime}</span>
        </>
      )}
    </div>
  );
}

// Iter115.1 / Iter117.3 — Last Market Scan pulse chip. Renders one of three
// states based on `lastScan` from `/api/market-scanner/status`:
//   1. `running` → progress %, blue (a live operation, not a freshness case).
//   2. `completed`/`cancelled`/`failed` → shared <FreshnessIndicator /> dot +
//      "Xm ago" + REFRESH button + counts (tradeable / universe).
//   3. null/none → nothing renders.
// Click on the chip body (NOT the Refresh button) navigates to /market-scanner.
function LastScanChip({ lastScan, navigate, onRefresh }) {
  if (!lastScan || !lastScan.status || lastScan.status === "none") {
    return <span data-testid="last-scan-chip-empty" />;
  }
  const running = lastScan.status === "running";
  const failed = lastScan.status === "failed";
  const cancelled = lastScan.status === "cancelled";

  // RUNNING — bespoke spinner + progress. Skip freshness widget.
  if (running) {
    const universe = Number(lastScan.universe_size || lastScan.pre_filtered || 0);
    const progress = Number(lastScan.progress_pct || 0);
    return (
      <button
        type="button"
        data-testid="last-scan-chip"
        data-status="running"
        onClick={() => navigate("/market-scanner")}
        title="Open Market Scanner"
        className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] hover:text-white border px-2.5 py-1 rounded-sm transition-colors border-[#007AFF]/50 text-[#007AFF] bg-[#007AFF]/5"
      >
        <Globe size={11} className="animate-spin" />
        <span>Scan running</span>
        <span className="text-[#3F3F47]">·</span>
        <span data-testid="last-scan-chip-progress" className="font-bold text-white">{progress}%</span>
        {universe > 0 && (
          <>
            <span className="text-[#3F3F47]">·</span>
            <span className="text-[#5F5F66] normal-case tracking-normal">of {universe.toLocaleString()}</span>
          </>
        )}
      </button>
    );
  }

  // FINISHED — shared FreshnessIndicator carries the dot + ago + Refresh.
  // The surrounding chip surfaces the counts and remains click-to-navigate.
  const when = lastScan.completed_at || lastScan.started_at;
  const fetchedAt = when ? new Date(when).getTime() : null;
  const opps = Number(lastScan.opportunities || 0);
  const universe = Number(lastScan.universe_size || lastScan.pre_filtered || 0);
  const accent = failed
    ? "border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5"
    : cancelled
    ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/5"
    : "border-[#00FF88]/30 text-[#00FF88] bg-[#00FF88]/5";

  return (
    <div
      data-testid="last-scan-chip"
      data-status={lastScan.status}
      role="button"
      tabIndex={0}
      onClick={() => navigate("/market-scanner")}
      onKeyDown={(e) => { if (e.key === "Enter") navigate("/market-scanner"); }}
      title="Open Market Scanner"
      className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] hover:text-white border px-2.5 py-1 rounded-sm transition-colors cursor-pointer ${accent}`}
    >
      <Globe size={11} />
      <span>{failed ? "Last scan failed" : cancelled ? "Scan cancelled" : "Last scan"}</span>
      <span className="text-[#3F3F47]">·</span>
      <FreshnessIndicator
        fetchedAt={fetchedAt}
        onRefresh={onRefresh}
        loading={false}
        testidPrefix="last-scan-chip-freshness"
        refreshTitle="Re-poll Market Scanner status"
      />
      {!failed && (
        <>
          <span className="text-[#3F3F47]">·</span>
          <span data-testid="last-scan-chip-opps" className="font-bold text-white">{opps.toLocaleString()}</span>
          <span className="text-[#5F5F66] normal-case tracking-normal">tradeable</span>
        </>
      )}
      {universe > 0 && (
        <>
          <span className="text-[#3F3F47]">·</span>
          <span className="text-[#5F5F66] normal-case tracking-normal">{universe.toLocaleString()} universe</span>
        </>
      )}
    </div>
  );
}
//
// Iter98 moved the Cockpit Safety Strip, CFM Inventory Context,
// and the five Controlled Auto-Scan panels to /dev/tools.
// Iter98.1 additionally moves the Wheel ITM / CRSP Candidates
// Visibility panel (a protected passthrough surface that
// duplicates the operator ticker grid below) and the Operator
// Guide tile (reference material — not needed in the daily
// trading cockpit). Both now live on /dev/tools.
//
// The Dashboard is now a clean Wheel-style operator cockpit:
// title + status + connection banner + Skip Reasons + watchlist
// input + ticker pills + Valid Only filter + Wheel-style ticker
// result cards + footer "Open Developer Tools" link.
export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user?.role === "admin";
  // Iter121.3 — Multi-watchlist wiring. Dashboard now respects the
  // user's active watchlist. Switching lists via the picker changes
  // the ticker grid + add/remove targets everywhere in one shot.
  const {
    watchlists,
    activeId: activeWatchlistId,
    switchActive: switchActiveWatchlist,
    refetch: refetchWatchlists,
  } = useActiveWatchlist();
  // Iter121.12 — Scope the live SSE scan to the active watchlist so the
  // trading-cards grid matches the ticker pills above. When
  // `activeWatchlistId` changes, the hook reconnects the SSE with the
  // new list. Passing `null` while the id is still loading lets the
  // backend fall back to the caller's active pointer.
  // Iter121.20 — Use the SHARED live stream from App-level context so
  // the scan persists across route changes. When the operator drills
  // into a ticker (Market Forge) and comes back, the Dashboard's
  // cards are already populated — no re-scan wait.
  const { results, status, lastUpdate, scanning, scanProgress } = useSharedLiveStream();
  const [watchlist, setWatchlist] = useState([]); const [connection, setConnection] = useState(null); const [newSymbol, setNewSymbol] = useState(""); const [addingSymbol, setAddingSymbol] = useState(false);
  const [showManageWatchlists, setShowManageWatchlists] = useState(false);
  const [hideNoCombo, setHideNoCombo] = useState(true);
  // Iter113 — "High Conviction" filter chip folded in from the
  // retired WES Trades / Live Scan tab.  When ON, only rows whose
  // hourly WES-P ≥ 7 AND WES-C ≥ 7 AND winning-scenario
  // `income_pct` ≥ 3 % remain visible.  Pure client-side filter.
  const [highConviction, setHighConviction] = useState(false);
  const [pinnedTicker, setPinnedTicker] = useState(null);
  // Iter113.1 — Outcome-pulse: last journal decision summary.
  // Read-only single-fetch on mount; null when no journal entries.
  const [lastDecision, setLastDecision] = useState(null);
  // Iter115.1 — Last Market Scan pulse chip.  Fetches a single
  // `/api/market-scanner/status` snapshot on mount so the operator
  // can see, at a glance, "Last scan · 12 m ago · 47 tradeable"
  // and jump straight to `/market-scanner`.  When a scan is in
  // flight, shows live progress instead.  No polling — reads once.
  const [lastScan, setLastScan] = useState(null);
  // Iter117.3 — exposed as a stable callable so the shared
  // FreshnessIndicator inside <LastScanChip /> can re-poll on demand
  // without restarting the whole Dashboard effect chain.
  const refetchLastScan = useCallback(() => {
    axios.get(`${API}/market-scanner/status`)
      .then(r => {
        const d = r?.data;
        if (d && d.status && d.status !== "none") setLastScan(d);
      })
      .catch(() => setLastScan(null));
  }, []);
  // Iter115.9 — Build-info chip state: single fetch of /api/health.
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    // Iter121.3 — Fetch scoped to the currently-active watchlist. When
    // the user switches lists via the picker, this effect re-runs and
    // the ticker grid rebuilds with the new symbols.
    if (activeWatchlistId) {
      axios.get(`${API}/watchlist`, { params: { watchlist_id: activeWatchlistId } })
        .then(r => setWatchlist(Array.isArray(r.data) ? r.data : []))
        .catch(() => setWatchlist([]));
    } else {
      axios.get(`${API}/watchlist`).then(r => setWatchlist(r.data)).catch(() => {});
    }
    if (isAdmin) {
      axios.post(`${API}/broker/test-connection`).then(r => setConnection(r.data)).catch(() => {});
    } else {
      setConnection({ connected: true, message: "Live stream active" });
    }
    // Iter113.1 — Pull just the most recent decision for the pulse chip.
    axios.get(`${API}/forge/journal/decisions?limit=1`)
      .then(r => {
        const rows = r?.data?.rows;
        setLastDecision(Array.isArray(rows) && rows.length > 0 ? rows[0] : null);
      })
      .catch(() => setLastDecision(null));
    // Iter115.1 — single-fetch last market scan summary.
    // Iter117.3 — extracted into a callable refetcher so the shared
    // FreshnessIndicator's Refresh button can re-poll on demand.
    refetchLastScan();
    // Iter115.9 — single-fetch backend health (version + uptime + db).
    axios.get(`${API}/health`)
      .then(r => { setHealth(r?.data || null); setHealthError(false); })
      .catch(err => {
        // 503 still includes the contract body — surface it as degraded
        // rather than offline so the operator sees the version/uptime.
        const body = err?.response?.data;
        if (body && typeof body === "object" && "status" in body) {
          setHealth(body);
          setHealthError(false);
        } else {
          setHealth(null);
          setHealthError(true);
        }
      });
  }, [isAdmin, refetchLastScan, activeWatchlistId]);
  const addSymbol = async () => {
    if (!newSymbol.trim()) return;
    setAddingSymbol(true);
    const upper = newSymbol.trim().toUpperCase();
    try {
      const params = activeWatchlistId ? { watchlist_id: activeWatchlistId } : {};
      await axios.post(`${API}/watchlist`, { symbol: upper }, { params });
      setNewSymbol("");
      const wl = await axios.get(`${API}/watchlist`, { params });
      setWatchlist(Array.isArray(wl.data) ? wl.data : []);
      await refetchWatchlists(activeWatchlistId);
      // Iter119.18 — toast feedback so the operator sees the add land
      // without staring at the watchlist for a fresh dot to flip on.
      toast.success(`${upper} added to watchlist`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not add ${upper}`);
    }
    setAddingSymbol(false);
  };
  const removeSymbol = async (symbol) => {
    try {
      const params = activeWatchlistId ? { watchlist_id: activeWatchlistId } : {};
      await axios.delete(`${API}/watchlist/${symbol}`, { params });
      setWatchlist(prev => prev.filter(w => w.symbol !== symbol));
      await refetchWatchlists(activeWatchlistId);
    } catch { /* ignore */ }
  };

  const handleTickerClick = (symbol) => {
    if (pinnedTicker === symbol) {
      setPinnedTicker(null);
    } else {
      setPinnedTicker(symbol);
      setTimeout(() => {
        const el = document.getElementById(`ticker-${symbol}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  const validCount = results.filter(r => r.recommendation === "OK").length;
  const hiddenCount = results.filter(r => r.recommendation !== "OK").length;
  // Iter113 — High-Conviction predicate (mirrors the retired
  // `WESTradesScan` "Actionable" filter).
  const isHighConviction = (r) => {
    if (r.recommendation !== "OK") return false;
    const wH = r.wes_hourly || r.wes;
    if (!wH || wH.wes_p < 7 || wH.wes_c < 7) return false;
    const best = r.winner === "ATM_CSP_ITM_CC" ? r.scenario_b : r.scenario_a;
    return !!best && best.income_pct >= 3;
  };
  const highConvCount = results.filter(isHighConviction).length;
  const filteredResults = (() => {
    let out = results;
    if (highConviction) {
      out = out.filter(r => isHighConviction(r) || r.symbol === pinnedTicker);
    } else if (hideNoCombo) {
      out = out.filter(r => r.recommendation === "OK" || r.symbol === pinnedTicker);
    }
    return out;
  })();
  const pinnedResult = pinnedTicker ? results.find(r => r.symbol === pinnedTicker) : null;

  return (
    <div data-testid="dashboard-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6"><div><h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo']">{t("dash.title")}</h2><p className="text-sm text-[#8A8A93] mt-1">{t("dash.subtitle")}</p></div><LiveIndicator status={status} scanning={scanning} lastUpdate={lastUpdate} /></div>
      <div className={`flex items-center gap-3 px-4 py-3 rounded-sm border mb-5 ${connection?.connected ? "border-[#00FF88]/20 bg-[#00FF88]/5" : "border-[#FF3366]/20 bg-[#FF3366]/5"}`}>{connection?.connected ? <><Wifi size={16} className="text-[#00FF88]" /><span className="text-sm text-[#00FF88] font-medium">{connection.message || t("dash.connected")}</span></> : <><WifiOff size={16} className="text-[#FF3366]" /><span className="text-sm text-[#FF3366]">{t("dash.not_connected")}</span><button onClick={() => navigate("/settings")} className="text-xs text-[#007AFF] ml-auto">{t("nav.settings")}</button></>}</div>
      <SkipReasonsWidget />
      {/* Iter121.3 — Watchlist picker: switching the active list here
          instantly rebuilds the ticker grid + steers subsequent adds/removes.
          Same source of truth as MarketForge, StrategyScannerV2, Bridge tile. */}
      <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="dashboard-watchlist-picker-row">
        <div className="w-full max-w-sm">
          <WatchlistPicker
            watchlists={watchlists}
            activeId={activeWatchlistId}
            onSwitch={switchActiveWatchlist}
            onManage={() => setShowManageWatchlists(true)}
            variant="expanded"
            testidPrefix="dash-wl-picker"
          />
        </div>
      </div>
      <div className="flex items-center gap-3 mb-3"><div className="flex gap-2 flex-1 max-w-sm"><input data-testid="add-symbol-input" type="text" value={newSymbol} onChange={e => setNewSymbol(e.target.value.toUpperCase())} onKeyDown={e => e.key === "Enter" && addSymbol()} placeholder={t("dash.add_ticker")} className="flex-1 bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] focus:outline-none text-white rounded-sm px-3 py-2 text-sm font-mono" /><button data-testid="add-symbol-button" onClick={addSymbol} disabled={addingSymbol || !newSymbol.trim()} className="bg-[#00FF88] text-[#08080A] px-3 py-2 rounded-sm hover:bg-[#33FF9F] disabled:opacity-40">{addingSymbol ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}</button></div>
      </div>
      {showManageWatchlists && (
        <ManageWatchlistsModal
          watchlists={watchlists}
          activeId={activeWatchlistId}
          onClose={() => setShowManageWatchlists(false)}
          onRefresh={() => refetchWatchlists(activeWatchlistId)}
          onSwitchActive={switchActiveWatchlist}
          testidPrefix="dash-wl-manage"
        />
      )}
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {watchlist.map(w => {
          const hasResult = results.find(r => r.symbol === w.symbol);
          const isValid = hasResult?.recommendation === "OK";
          const isPinned = pinnedTicker === w.symbol;
          const isPersonal = w.scope === "personal";
          const canDelete = isAdmin || isPersonal;
          return (
            <div key={w.symbol} onClick={() => handleTickerClick(w.symbol)}
              role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleTickerClick(w.symbol); } }}
              data-testid={`wl-pill-${w.symbol}`}
              data-scope={w.scope || "shared"}
              title={isPersonal ? "Personal — only you see this ticker" : "Shared — admin watchlist"}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-mono transition-all cursor-pointer group border ${
                isPinned ? "border-[#00FF88] bg-[#00FF88]/10 text-[#00FF88]" :
                isValid ? "border-[#26262B] bg-[#121216] text-[#E0E0E0] hover:border-[#00FF88]/50 hover:bg-[#00FF88]/5" :
                hasResult ? "border-[#26262B] bg-[#121216] text-[#8A8A93] hover:border-[#FFB800]/30" :
                "border-[#26262B] bg-[#121216] text-[#3F3F47]"
              }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${isValid ? "bg-[#00FF88]" : hasResult ? "bg-[#FF3366]" : "bg-[#3F3F47]"}`} />
              <div
                data-testid={`wl-pill-scope-${w.symbol}`}
                className={`w-1 h-1 rounded-full ${isPersonal ? "bg-[#FFB800]" : "bg-[#3BB5FF]/70"}`}
              />
              {w.symbol}
              {canDelete && (
                <button type="button" onClick={(e) => { e.stopPropagation(); removeSymbol(w.symbol); }} className="text-[#8A8A93] hover:text-[#FF3366] opacity-0 group-hover:opacity-100 ml-0.5"><Trash2 size={9} /></button>
              )}
            </div>
          );
        })}
      </div>
      {/* Iter121.14 — Live scan progress line. Shows N/M cycled through
          the active watchlist so the operator can see the SSE is alive
          even when broker-side data is slow or unavailable. Renders as
          a compact chip strip so it fits between the ticker grid and
          the filter toolbar without adding visual weight. */}
      {(scanProgress?.total > 0 || scanning) && (() => {
        const total = scanProgress?.total || 0;
        const done = Math.min(scanProgress?.done || 0, total || Infinity);
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        const activeList = (watchlists || []).find(w => w.id === activeWatchlistId);
        const activeName = activeList?.name || "Active";
        const inFlight = scanning && !scanProgress?.complete && total > 0 && done < total;
        const label = scanProgress?.complete
          ? `Scan complete · ${done}/${total}`
          : inFlight
            ? `Scanning ${done}/${total}${scanProgress?.symbol ? ` · ${scanProgress.symbol}` : ""}`
            : `Ready · ${total} tickers`;
        return (
          <div
            data-testid="dashboard-scan-progress"
            className="flex items-center gap-3 mb-4 flex-wrap text-[10px] font-mono uppercase tracking-[0.12em]"
          >
            <span
              data-testid="dashboard-scan-progress-label"
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border ${
                inFlight
                  ? "border-[#00FF88]/40 bg-[#00FF88]/5 text-[#00FF88]"
                  : "border-[#26262B] bg-[#08080A] text-[#8A8A93]"
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  inFlight ? "bg-[#00FF88] animate-pulse" : "bg-[#3F3F47]"
                }`}
              />
              {label}
            </span>
            <span
              data-testid="dashboard-scan-progress-watchlist"
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#26262B] bg-[#08080A] text-[#8A8A93]"
              title="Active watchlist scoping this live scan"
            >
              <span className="text-[#5F5F66]">Watchlist:</span>
              <span className="text-[#E4E4E7]">{activeName}</span>
            </span>
            {total > 0 && (
              <div
                data-testid="dashboard-scan-progress-bar"
                className="h-1 bg-[#1A1A20] rounded-full flex-1 min-w-[80px] max-w-[240px] overflow-hidden border border-[#26262B]"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  data-testid="dashboard-scan-progress-bar-fill"
                  className={`h-full transition-all duration-300 ${
                    scanProgress?.complete ? "bg-[#3F3F47]" : "bg-[#00FF88]"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            )}
          </div>
        );
      })()}
      {results.length > 0 && hiddenCount > 0 && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button
            data-testid="filter-toggle"
            onClick={() => { setHideNoCombo(!hideNoCombo); if (!hideNoCombo) setPinnedTicker(null); }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-[11px] font-mono font-bold border transition-colors ${
              hideNoCombo ? "border-[#00FF88]/30 text-[#00FF88] bg-[#00FF88]/5" : "border-[#3F3F47] text-[#8A8A93]"
            }`}>
            {hideNoCombo ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
            {hideNoCombo ? t("dash.valid_only") : t("dash.show_all")}
          </button>
          {/* Iter113 — High-Conviction chip (folded in from /trades) */}
          <button
            data-testid="filter-high-conviction"
            onClick={() => setHighConviction(v => !v)}
            title={t("dash.high_conviction_hint") || "WES-P ≥ 7 · WES-C ≥ 7 · income ≥ 3 %"}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-[11px] font-mono font-bold border transition-colors ${
              highConviction
                ? "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10"
                : "border-[#3F3F47] text-[#8A8A93] hover:border-[#FFB800]/30 hover:text-[#FFB800]"
            }`}>
            <Flame size={12} fill={highConviction ? "currentColor" : "none"} />
            {t("dash.high_conviction") || "High Conviction"} · {highConvCount}
          </button>
          <span className="text-[10px] font-mono text-[#8A8A93]">
            {validCount} valid · {hiddenCount} hidden
            {pinnedTicker && (hideNoCombo || highConviction) && pinnedResult?.recommendation !== "OK" && (
              <span className="text-[#FFB800] ml-2">+ {pinnedTicker} pinned</span>
            )}
          </span>
        </div>
      )}
      {filteredResults.length === 0 && results.length === 0 ? (
        scanning ? (
          /* Iter119.18 — Skeleton ticker-card list while the initial
             scan is in flight. Replaces the lonely centered spinner;
             gives the operator a content-shaped preview so the page
             feels live the moment it loads. */
          <SkeletonList count={3} testid="dashboard-scanning-skeleton" />
        ) : (
          <div className="bg-[#121216] border border-[#26262B] rounded-sm p-12 text-center">
            <ScanLine size={40} className="text-[#8A8A93] mx-auto mb-3" />
            <p className="text-[#8A8A93] text-sm">Waiting for live data...</p>
          </div>
        )
      ) : filteredResults.length === 0 ? <div className="bg-[#121216] border border-[#26262B] rounded-sm p-8 text-center"><p className="text-[#8A8A93] text-sm">No tickers with valid combos. Click a ticker above to inspect it.</p></div>
      : <div className="space-y-4">{filteredResults.map(r => <TickerCard key={r.symbol} r={r} />)}</div>}

      {/* Phase 6B.1 / Iter98 — small footer link out to all the
          moved diagnostic + control tools. Operator-facing dashboard
          stays clean; advanced controls live under /dev/tools.
          Iter113.1 — Outcome-pulse chip on the left renders "Last
          decision: <symbol> · <status> · <when>" when the operator
          has a Decision Journal entry; clicks navigate to /journal. */}
      <div
        data-testid="dashboard-developer-tools-link"
        className="mt-8 pt-4 border-t border-[#26262B] flex items-center justify-between gap-2 flex-wrap"
      >
        {lastDecision ? (
          <button
            type="button"
            data-testid="outcome-pulse-chip"
            onClick={() => navigate("/journal")}
            className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] border border-[#26262B] hover:border-[#3F3F47] px-2.5 py-1 rounded-sm transition-colors"
            title={t("dash.outcome_pulse_hint") || "Open Decision Journal"}
          >
            <BookmarkCheck size={11} className="text-[#FFD700]" />
            <span>{t("dash.last_decision") || "Last decision"}</span>
            <span data-testid="outcome-pulse-symbol" className="font-bold text-white">{lastDecision.symbol}</span>
            <span className="text-[#3F3F47]">·</span>
            <span
              data-testid="outcome-pulse-status"
              className="font-bold"
              style={{ color: STATUS_COLORS[lastDecision.status] || "#8A8A93" }}
            >
              {lastDecision.status}
            </span>
            <span className="text-[#3F3F47]">·</span>
            <span data-testid="outcome-pulse-when" className="text-[#5F5F66] normal-case tracking-normal">
              {relativeTime(lastDecision.created_at)}
            </span>
          </button>
        ) : <span data-testid="outcome-pulse-empty" />}
        {/* Iter115.1 — Last Market Scan pulse chip. */}
        <LastScanChip lastScan={lastScan} navigate={navigate} onRefresh={refetchLastScan} />
        {/* Iter115.9 — Build-info chip (service status + version + uptime). */}
        <BuildInfoChip health={health} error={healthError} />
        <button
          type="button"
          onClick={() => navigate("/dev/tools")}
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0] border border-[#26262B] hover:border-[#3F3F47] px-2.5 py-1 rounded-sm"
        >
          <Wrench size={11} />
          Need diagnostics? Open Developer Tools
        </button>
      </div>
    </div>
  );
}
